import React from 'react';
import { FileText } from 'lucide-react';

const TechnicalBlueprintWizard = ({ data, setData }) => {
  const options = [
    { id: 'dataDict',     label: 'Diccionario de Datos',        desc: 'Tablas Z, estructuras y elementos de datos' },
    { id: 'classDiagram', label: 'Arquitectura de Clases (UML)', desc: 'Jerarquía de clases e interfaces ABAP' },
    { id: 'testPlan',     label: 'Plan de Pruebas Unitarias',    desc: 'Casos de prueba y escenarios de validación' },
    { id: 'estimations',  label: 'Estimación de Esfuerzo',       desc: 'Horas por tarea y cronograma sugerido' },
  ];

  return (
    <div className="animate-in fade-in slide-in-from-top-4 duration-500 text-left space-y-6">

      {/* NOTA */}
      <div className="bg-slate-900/30 border-l-4 border-slate-400 p-5 rounded-r-3xl flex gap-4 items-start">
        <div className="bg-slate-600 p-2 rounded-xl text-white shrink-0">
          <FileText size={18} />
        </div>
        <div>
          <p className="text-[11px] text-slate-300 font-black uppercase tracking-widest mb-1">
            Technical Blueprint Guide
          </p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Esta herramienta actúa como un <strong>SAP Solutions Architect</strong>. 
            Transforma una necesidad de negocio en una arquitectura técnica completa. 
            Define objetos del diccionario (Tablas Z, Estructuras), jerarquía de clases, 
            interfaces y planes de prueba. Genera un <strong>Documento de Diseño Técnico (TDD)</strong> 
            estructurado y listo para ser entregado al equipo de desarrollo.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* COLUMNA 1 */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-slate-500 space-y-4">
          <label className="text-[10px] font-black text-slate-300 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <FileText size={14}/> Configuración del Proyecto
          </label>

          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">
              Título del Proyecto / Requerimiento
            </label>
            <input
              type="text"
              className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-xl text-xs text-white placeholder:text-slate-600 outline-none focus:border-slate-400"
              placeholder="Ej: Interfaz de Facturación Electrónica"
              value={data.bpTitle || ''}
              onChange={(e) => setData({...data, bpTitle: e.target.value})}
            />
          </div>

          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">
              Módulos SAP Afectados
            </label>
            <input
              type="text"
              className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-xl text-xs text-white placeholder:text-slate-600 outline-none focus:border-slate-400"
              placeholder="Ej: SD, FI, MM, HCM"
              value={data.bpModules || ''}
              onChange={(e) => setData({...data, bpModules: e.target.value})}
            />
          </div>

          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">
              Tipo de Desarrollo
            </label>
            <select
              className="w-full p-2.5 bg-slate-800 border-none rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-slate-500"
              value={data.bpType || 'report'}
              onChange={(e) => setData({...data, bpType: e.target.value})}
            >
              <option value="report">Reporte / ALV</option>
              <option value="interface">Interfaz / API</option>
              <option value="enhancement">Enhancement / BAdI</option>
              <option value="conversion">Conversión de Datos</option>
              <option value="form">Formulario / Smart Form</option>
              <option value="workflow">Workflow SAP</option>
              <option value="fiori">App Fiori / BTP</option>
            </select>
          </div>

          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">
              Complejidad Estimada
            </label>
            <select
              className="w-full p-2.5 bg-slate-800 border-none rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-slate-500"
              value={data.bpComplexity || 'medium'}
              onChange={(e) => setData({...data, bpComplexity: e.target.value})}
            >
              <option value="low">Baja — 1 a 3 días</option>
              <option value="medium">Media — 3 a 10 días</option>
              <option value="high">Alta — 10 a 30 días</option>
              <option value="critical">Crítica — +30 días</option>
            </select>
          </div>
          
          {/* Campo adicional para vista CDS relacionada, opcional pero útil para generar un blueprint más preciso */}
          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">
              Vista CDS Relacionada (opcional)
            </label>
            <input
              type="text"
              className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-xl text-xs font-mono text-white placeholder:text-slate-600 outline-none focus:border-slate-400 uppercase"
              placeholder="Ej: ZI_SalesOrder o ZC_PurchaseOrder"
              value={data.bpCdsView || ''}
              onChange={(e) => setData({...data, bpCdsView: e.target.value})}
            />
          </div>

        </div>
         
        {/* COLUMNA 2 */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-slate-500 space-y-4">
          <label className="text-[10px] font-black text-slate-300 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <FileText size={14}/> Secciones del Documento
          </label>

          <div className="space-y-3">
            {options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setData({
                  ...data,
                  bpOptions: { ...data.bpOptions, [opt.id]: !data.bpOptions?.[opt.id] }
                })}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                  data.bpOptions?.[opt.id]
                    ? 'bg-slate-600/20 border-slate-500/50'
                    : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className={`w-2 h-2 rounded-full shrink-0 ${data.bpOptions?.[opt.id] ? 'bg-slate-300 animate-pulse' : 'bg-slate-600'}`} />
                <div className="flex-1">
                  <p className={`text-[10px] font-black uppercase ${data.bpOptions?.[opt.id] ? 'text-slate-300' : 'text-slate-400'}`}>
                    {opt.label}
                  </p>
                  <p className="text-[9px] text-slate-500">{opt.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalBlueprintWizard;